<script lang="ts">
const POLAND_LAUNCH_DISCOUNT_CODE = 'XJQWZK'
</script>
<script lang="ts" setup>
import type { SetupContext, SlotsType } from 'vue'

import { Fragment } from 'vue'
import LineX from '@docue/docue-ui-v2/icons/LineX.vue'
import SolidSale02 from '@docue/docue-ui-v2/icons/SolidSale02.vue'
import LineAlertCircle from '@docue/docue-ui-v2/icons/LineAlertCircle.vue'
import SolidMessageChatCircle from '@docue/docue-ui-v2/icons/SolidMessageChatCircle.vue'

import type { PriceListAddon } from '~/composables/api/price-list/useFetchPriceList'
import type { EstimatePayload } from '~/composables/api/subscription/estimate/useFetchSubscriptionEstimate'

const props = defineProps<{
  selectedPlanPriceId: string
  disabled?: boolean
  isSubscriptionCreated?: boolean
}>()

const { t } = useI18n()
const { formatCurrency } = useIntl()
const { country } = useAuth()
const { showIntercom } = useSubscriptionProcessContext()
const { translateForCountry } = useCountryAwareTranslation()

const { data: storyBlokConfig } = useQueryStoryblok.getAppConfig({
  country,
})



const hasFaqItems = computed(() => !!(storyBlokConfig.value?.story.content.faq_items ?? []).length)

const discountCode = defineModel<string>({ required: true })

const isDiscountCodeInputVisible = ref(!!discountCode.value)

whenever(discountCode, () => (isDiscountCodeInputVisible.value = true))

const isFaqModalOpen = ref(false)

const {
  selectedPlan,
  paymentPeriodUnit,
  priceList,
  selectedAddonsForEstimate,
} = useSubscriptionProcessContext()

const availableAddons = computed(() => priceList.dataWithoutUpgrades.value?.addons)

const internalCoupon = ref('')

watch(internalCoupon, (val, oldVal) => {
  if (!val && oldVal && discountCode.value)
    discountCode.value = ''
})

const summaryRoot = ref<HTMLDivElement>()
const isSummaryRootVisible = useElementVisibility(summaryRoot)

const estimatePayload = computed<EstimatePayload>(() => ({
  planPriceId: props.selectedPlanPriceId,
  coupon: discountCode.value || undefined,
  addons: selectedAddonsForEstimate.value,
}))

const {
  data: estimate,
  isPending: isEstimatePending,
  isError: isEstimateError,
} = useQuerySubscriptionEstimate.getEstimate({
  payload: estimatePayload,
  useQueryOptions: {
    enabled: () => isSummaryRootVisible.value && !!props.selectedPlanPriceId && !props.isSubscriptionCreated,
  },
})

const isEstimatePendingDelayed = refDelayed(isEstimatePending, false)

const featureConfigsForAddons = computed(() => (priceList.dataWithoutUpgrades.value?.data?.feature_configs ?? [])
  .flatMap(({ options }) =>
    options.flatMap(({ options }) =>
      options.flatMap(opt => opt.addon_id ? [{ addon_id: opt.addon_id, quantity: opt.quantity }] : []),
    ),
  ),
)

const getAddonTitle = (addon: PriceListAddon, quantity: number) => {
  const addonFeatureConfig = featureConfigsForAddons.value.find(({ addon_id }) => addon_id === addon.addon_id)
  if (addonFeatureConfig) {
    return t('subscribe.summary.signature-message', { count: addonFeatureConfig.quantity })
  }

  switch (addon.addon_id) {
    case 'workspace-viewer-pt':
      return t('subscribe.summary.viewer-license-title', { n: quantity })
    case 'extra-users-pt':
      return t('subscribe.summary.user-license-title', { n: quantity })
    case 'unlimited-contracts-pt':
      return t('subscribe.summary.unlimited-contracts-title')
    case 'unlimited-template-access-pt':
      return translateForCountry('subscribe.subscriptionCartSummary.unlimitedTemplateAccess')
    case 'unlimited-e-signing-pt':
      return t('subscribe.summary.unlimited-e-signing-title')
    case 'user':
      return t('subscribe.summary.user-seats-message', { n: quantity, quantity })
    default:
      return addon.title
  }
}

const getEstimateLineTitle = ({ id, type, quantity, addon }: { id: string, type: string, quantity: number, addon?: PriceListAddon }) => {
  if (props.selectedPlanPriceId === id)
    return selectedPlan.value?.title

  if (type !== 'addon') {
    return undefined
  }

  if (!addon)
    return undefined

  const price = addon.prices.find(({ price_id }) => price_id === id)

  if (!price)
    return undefined

  return getAddonTitle(addon, quantity)
}

const getEntityName = (addon: PriceListAddon) => {
  switch (addon.addon_id) {
    case 'workspace-viewer-pt':
      return t('viewer')
    case 'extra-users-pt':
      return t('user')
    default:
      return addon.title
  }
}

const getEstimateLinePeriodText = ({ unitAmount, quantity, addon }: { unitAmount: number, quantity: number, addon?: PriceListAddon }) => {
  if (priceList.data.value?.data_version === 'v1.1' && quantity > 1 && addon) {
    return paymentPeriodUnit.value === 'year'
      ? t('subscribe.summary.line-period-text.annual', { price: formatCurrency((unitAmount / 12) / 100, { minimumFractionDigits: 0 }), entity: getEntityName(addon) })
      : t('subscribe.summary.line-period-text.monthly', { price: formatCurrency(unitAmount / 100, { minimumFractionDigits: 0 }), entity: getEntityName(addon) })
  }

  if (paymentPeriodUnit.value === 'year') {
    return priceList.data.value?.data_version === 'v1.1'
      ? `(${formatCurrency((unitAmount / 12) / 100, { minimumFractionDigits: 0 })} x 12)`
      : t('subscribe.summary.annual-period-text', {
          price: formatCurrency((unitAmount / 12) / 100, { minimumFractionDigits: 0 }),
        })
  }

  return priceList.data.value?.data_version === 'v1'
    ? t('subscribe.summary.monthly-period-text')
    : undefined
}

const estimatePriceRows = computed(() => {
  if (!estimate.value || isEstimatePending.value)
    return []

  return estimate.value.lines.map(({ id, unitAmount, quantity, type }) => {
    const addon = availableAddons.value?.find(({ prices }) => prices.find(({ price_id }) => price_id === id))

    if (type === 'addon' && !addon)
      return undefined

    return {
      id,
      title: getEstimateLineTitle({ id, type, quantity, addon }),
      text: priceList.data.value?.data_version !== 'v2' ? getEstimateLinePeriodText({ quantity, unitAmount, addon }) : undefined,
      amount: (unitAmount * quantity) / 100,
    }
  }).filter(Boolean) as { id: string, title: string, text?: string, amount: number }[]
})

const activeCoupon = computed(() => estimate.value?.activeCoupon)

const discountCodeErrorMessage = computed(() => {
  if (
    isEstimatePending.value
    || (discountCode.value && !internalCoupon.value)
    || !discountCode.value
    || activeCoupon.value
  ) {
    return undefined
  }

  return t('subscribe.cart.invalid-discount-code')
})

const totalDiscountAmount = computed(() => estimate.value?.discounts.reduce((acc, row) => acc + row.amount, 0) || 0)

const SummaryListRow = (
  { pending }: { pending: boolean },
  { slots }: SetupContext<null, SlotsType<{ default: () => any }>>,
) =>
  h(
    Fragment,
    pending
      ? h(
          'div',
          { class: 'col-span-full h-[1lh] animate-pulse bg-gray-200/40' },
          {},
        )
      : slots.default(),
  )
</script>

<template>
  <LazySubscriptionFaqModal
    v-if="isFaqModalOpen"
    v-model="isFaqModalOpen"
  />
  <div
    ref="summaryRoot"
    class="flex flex-col gap-6 rounded-lg border border-gray-200 p-6"
  >
    <span class="text-xl font-semibold">{{ t('subscribe.summary.title') }}</span>

    <DTAlert
      v-if="isEstimateError"
      variant="red"
    >
      <LineAlertCircle class="size-4 h-[1lh] shrink-0" />
      <span>{{ t('subscribe.summary.error') }}</span>
    </DTAlert>

    <div
      v-else
      class="flex flex-col gap-3"
    >
      <DTButton
        v-if="!isDiscountCodeInputVisible && !isSubscriptionCreated"
        variant="link"
        size="sm"
        dense
        class="max-w-max"
        :disabled
        @click="isDiscountCodeInputVisible = true"
      >
        {{ t('subscribe.summary.add-discount') }}
      </DTButton>

      <form
        v-if="isDiscountCodeInputVisible && !isSubscriptionCreated"
        id="cartSummary"
        @submit.prevent="discountCode = internalCoupon"
      >
        <DTField
          :label="t('discount-code')"
          size="sm"
          :error="discountCodeErrorMessage"
        >
          <DTFieldGroup>
            <DTFieldInputWrapper>
              <DTFieldInput
                v-model="internalCoupon"
                class="min-w-0"
                wrapper-class="min-w-0"
                :disabled="!!activeCoupon || disabled"
              />
              <DTFieldInputInlineAddon v-if="activeCoupon">
                <DTIconButton
                  size="sm"
                  variant="plain"
                  @click="() => {
                    internalCoupon = ''
                    discountCode = ''
                  }"
                >
                  <LineX class="size-3.5" />
                </DTIconButton>
              </DTFieldInputInlineAddon>
            </DTFieldInputWrapper>
            <DTFieldGroupButton
              v-if="!activeCoupon"
              :disabled="disabled"
              class="disabled:cursor-not-allowed"
              type="submit"
            >
              {{ t('use') }}
            </DTFieldGroupButton>
          </DTFieldGroup>
        </DTField>
      </form>

      <dl
        class="grid grid-cols-[auto,max-content] gap-x-2 gap-y-3 text-sm"
      >
        <SummaryListRow :pending="isEstimatePendingDelayed">
          <template
            v-for="row in estimatePriceRows"
            :key="row.id"
          >
            <dt>
              {{ row.title }} <span class="text-gray-500">{{ row.text }}</span>
            </dt>
            <dd class="ms-auto font-semibold">
              {{ formatCurrency(row.amount, { minimumFractionDigits: 0 }) }}
            </dd>
          </template>
        </SummaryListRow>

        <SummaryListRow
          v-if="activeCoupon"
          :pending="isEstimatePendingDelayed"
        >
          <dt class="flex items-center gap-0.5">
            <span class="line-clamp-1 break-all">
              {{ t('subscribe.summary.discount-line', { coupon: activeCoupon.id }) }}
            </span>
            <DTTooltip
              v-if="activeCoupon.description"
              :content="activeCoupon.description"
            >
              <LineAlertCircle
                class="size-3.5"
              />
            </DTTooltip>
          </dt>
          <dd class="ms-auto font-semibold">
            {{ formatCurrency(-(activeCoupon.amount / 100), { minimumFractionDigits: 0 }) }}
          </dd>
        </SummaryListRow>

        <DTDivider class="col-span-full my-3" />

        <SummaryListRow :pending="isEstimatePendingDelayed">
          <dt>
            {{ t('subtotal') }}
          </dt>
          <dd class="ms-auto font-semibold">
            {{ formatCurrency((estimate?.amountTotal ?? 0) / 100, { minimumFractionDigits: 0 }) }}
          </dd>
        </SummaryListRow>

        <SummaryListRow :pending="isEstimatePendingDelayed">
          <dt>
            {{ t('vat.title') }}
          </dt>
          <dd class="ms-auto font-semibold">
            {{ formatCurrency((estimate?.taxAmount ?? 0) / 100, { minimumFractionDigits: 0 }) }}
          </dd>
        </SummaryListRow>

        <SummaryListRow
          v-if="totalDiscountAmount"
          :pending="isEstimatePendingDelayed"
        >
          <dt>
            {{ t('subscribe.cart.compensation') }}
          </dt>
          <dd class="ms-auto font-semibold">
            {{ formatCurrency(-(totalDiscountAmount / 100), { minimumFractionDigits: 0 }) }}
          </dd>
        </SummaryListRow>

        <DTDivider class="col-span-full my-3" />

        <SummaryListRow :pending="isEstimatePendingDelayed">
          <dt class="text-base">
            {{ props.isSubscriptionCreated ? t('paid') : t('subscribe.summary.amount-due-text') }}
          </dt>
          <dd class="ms-auto text-base font-semibold">
            {{ formatCurrency((estimate?.amountDue ?? 0) / 100, { minimumFractionDigits: 0 }) }}
          </dd>
        </SummaryListRow>
      </dl>
    </div>
  </div>

  <div
    v-if="country === 'PL' && activeCoupon?.id !== POLAND_LAUNCH_DISCOUNT_CODE"
    class="flex items-center gap-3 rounded-lg bg-green-600 p-4"
  >
    <SolidSale02 class="size-5 shrink-0 text-green-200" />
    <i18n-t
      keypath="subscribe.summary.discountCouponText"
      tag="p"
      class="text-sm text-white"
    >
      <template #discount>
        -50%
      </template>
      <template #coupon>
        <strong>{{ POLAND_LAUNCH_DISCOUNT_CODE }}</strong>
      </template>
    </i18n-t>
    <button
      class="ml-auto mr-0 min-w-max text-sm font-semibold text-white underline"
      @click="() => {
        internalCoupon = POLAND_LAUNCH_DISCOUNT_CODE
        discountCode = POLAND_LAUNCH_DISCOUNT_CODE
      }"
    >
      {{ capitalize(t('subscribe.summary.applyCode')) }}
    </button>
  </div>

  <div class="flex items-center gap-3 rounded-lg bg-gray-50 p-4">
    <SolidMessageChatCircle class="size-5 shrink-0" />
    <p class="text-sm">
      <i18n-t
        v-if="hasFaqItems"
        keypath="subscribe.summary.help-text"
      >
        <template #chatTrigger>
          <DTButton
            size="sm"
            variant="link"
            dense
            class="!font-semibold !text-gray-800"
            @click="showIntercom"
          >
            {{ t('chat.text') }}
          </DTButton>
        </template>
        <template #faqTrigger>
          <DTButton
            variant="link"
            dense
            size="sm"
            class="!font-semibold !text-gray-800"
            @click="isFaqModalOpen = true"
          >
            {{ t('faq') }}
          </DTButton>
        </template>
      </i18n-t>
      <i18n-t
        v-else
        keypath="subscribe.summary.get-help"
      >
        <template #chatTrigger>
          <DTButton
            size="sm"
            variant="link"
            dense
            class="!font-semibold !text-gray-800"
            @click="showIntercom"
          >
            {{ t('chat.text') }}
          </DTButton>
        </template>
      </i18n-t>
    </p>
  </div>
</template>
